import { useState } from 'react';
import { rsme, type Lead } from '../lib/rsme';
import { useQuery } from '../ui/useQuery';
import { PageHead, Card, Pill, Input, Loading, ErrorState, EmptyState } from '../ui/primitives';
import { useAiko } from '../aiko/store';

const STATUSES: Lead['status'][] = ['new', 'contacted', 'qualified', 'converted', 'lost'];

export default function Leads() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<Lead['status'] | ''>('');
  const ask = useAiko((s) => s.ask);
  const q = useQuery(() => rsme.leads.list({ search, status: status || undefined }), [search, status]);

  return (
    <div className="max-w-5xl mx-auto">
      <PageHead title="Leads" sub="Inbox pipeline · Sales X drafts the quotes, you decide" />
      <div className="px-5 md:px-8 flex flex-wrap gap-2 items-center">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, company, phone, email" className="md:max-w-sm" />
        <select value={status} onChange={(e) => setStatus(e.target.value as any)} className="h-10 px-3 rounded-lg bg-elevated border border-line text-sm">
          <option value="">all</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="px-5 md:px-8 mt-4 space-y-2">
        {q.loading && <Loading rows={5} />}
        {q.error && <ErrorState error={q.error} retry={q.refetch} />}
        {!q.loading && !q.error && !q.data?.data?.length && <EmptyState title="No leads" />}
        {q.data?.data?.map((l) => (
          <Card key={l.id} className="!p-4">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="w-9 h-9 rounded-full bg-elevated grid place-items-center font-semibold">{(l.name?.[0] ?? '·').toUpperCase()}</div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium truncate">{l.name}</span>
                  <Pill tone={l.status === 'new' ? 'accent' : l.status === 'contacted' ? 'warn' : l.status === 'qualified' ? 'good' : l.status === 'converted' ? 'good' : 'muted'}>{l.status}</Pill>
                </div>
                <div className="text-xs text-muted mt-0.5">{l.company ?? '—'} · {l.phone ?? l.email ?? '—'} · {l.source ?? 'source unknown'}</div>
                {l.tonnageRequired && <div className="text-xs text-muted">needs {l.tonnageRequired}T for {l.rentalPeriod ?? 'unspecified period'}</div>}
              </div>
              <button onClick={() => ask(`Draft a quotation for ${l.name} (${l.company ?? '?'}, needs ${l.tonnageRequired ?? '?'}T for ${l.rentalPeriod ?? '?'}). Use Sales X knowledge base + current rates.`)} className="text-xs text-accent hover:underline">draft quote</button>
            </div>
            {l.notes && <div className="text-xs text-muted mt-2 line-clamp-2">{l.notes}</div>}
          </Card>
        ))}
      </div>
    </div>
  );
}
